<template>
  <div class="contract-modal-host">
    <div class="modal fade" id="contract-create-modal" tabindex="-1" aria-labelledby="contract-create-modal-title" aria-hidden="true">
        <div class="modal-dialog modal-fullscreen contract-dialog contract-dialog--editor">
            <div class="modal-content modern-modal">
                <div class="modal-header contract-modal-header">
                    <div class="d-flex align-items-start gap-3">
                        <span class="modal-title-icon">
                            <AppIcon name="bi-file-earmark-text" class="bi bi-file-earmark-text me-2" />
                        </span>
                        <div>
                            <h5 id="contract-create-modal-title" class="modal-title mb-1">
                                {{ contract.id ? $t('edit_contract') : (isTemplateMode ? $t('create_contract_template') : $t('create_contract')) }}
                            </h5>
                            <p class="modal-subtitle mb-0">
                                {{ modalSubtitle }}
                            </p>
                        </div>
                    </div>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                </div>

                <div v-if="!isTemplateMode && !contract.id" class="contract-progress">
                    <template v-for="(step, index) in creationSteps">
                        <div :key="step.key" class="contract-progress-step" :class="{ active: currentStep === index + 1, complete: currentStep > index + 1 }">
                            <span class="contract-progress-number">
                                <AppIcon v-if="currentStep > index + 1" name="bi-check-lg" class="bi bi-check-lg" />
                                <span v-else>{{ index + 1 }}</span>
                            </span>
                            <span>{{ step.label }}</span>
                        </div>
                        <span v-if="index < creationSteps.length - 1" :key="`${step.key}-line`" class="contract-progress-line" :class="{ complete: currentStep > index + 1 }"></span>
                    </template>
                </div>

                <div class="modal-body contract-modal-body">
                    <div v-if="activeTab === 'template'" class="template-step-workspace">
                        <div class="step-intro">
                            <span class="step-intro-icon"><AppIcon name="bi-files" class="bi bi-files" /></span>
                            <div>
                                <h5 class="mb-1">{{ $t('choose_contract_template') }}</h5>
                                <p class="mb-0">{{ $t('choose_contract_template_help') }}</p>
                            </div>
                        </div>

                        <div v-if="templatesLoading" class="template-loading-state">
                            <span class="spinner-border text-primary" role="status" aria-hidden="true"></span>
                            <span>{{ $t('loading') }}</span>
                        </div>
                        <div v-else class="template-choice-grid">
                            <button
                                v-for="template in templateChoices"
                                :key="template.value"
                                type="button"
                                class="template-choice-card"
                                :class="{ selected: selectedTemplateId === template.value }"
                                :aria-pressed="selectedTemplateId === template.value ? 'true' : 'false'"
                                @click="selectTemplateChoice(template.value)"
                            >
                                <span class="template-choice-preview" :class="`template-choice-preview--${template.preview}`">
                                    <span class="preview-header-line"></span>
                                    <span class="preview-title-line"></span>
                                    <span class="preview-copy-line"></span>
                                    <span class="preview-copy-line short"></span>
                                    <span class="preview-signature-line"></span>
                                </span>
                                <span class="template-choice-content">
                                    <span class="template-choice-title">
                                        <AppIcon :name="template.icon" class="bi" :class="template.icon" />
                                        <strong>{{ template.label }}</strong>
                                    </span>
                                    <small>{{ template.description }}</small>
                                </span>
                                <span class="template-choice-check">
                                    <AppIcon v-if="selectedTemplateId === template.value" name="bi-check-lg" class="bi bi-check-lg" />
                                </span>
                            </button>
                        </div>
                    </div>

                    <div v-show="activeTab === 'general'" class="setup-workspace">
                        <section class="setup-card">
                            <div v-if="isTemplateMode" class="row g-3">
                                <div class="col-12">
                                    <label class="form-label fw-semibold">{{ $t('template_name') }} <span class="text-danger">*</span></label>
                                    <input v-model.trim="contract.title" type="text" class="form-control form-control-lg" :placeholder="$t('contract_title_placeholder')" autofocus>
                                </div>
                            </div>

                            <template v-else>
                                <div class="section-heading">
                                    <span class="section-number">1</span>
                                    <div>
                                        <h6 class="mb-0">{{ $t('essential_details') }}</h6>
                                        <small class="text-muted">{{ $t('essential_details_help') }}</small>
                                    </div>
                                </div>
                                <div class="row g-3">
                                    <div class="col-md-8">
                                        <label class="form-label fw-semibold">{{ $t('title') }} <span class="text-danger">*</span></label>
                                        <input v-model.trim="contract.title" type="text" class="form-control" :placeholder="$t('contract_title_placeholder')" autofocus>
                                    </div>
                                    <div class="col-md-4">
                                        <label class="form-label fw-semibold">{{ $t('start_date') }} <span class="text-danger">*</span></label>
                                        <input v-model="contract.start_date" type="date" class="form-control">
                                    </div>
                                </div>

                                <div class="section-heading section-heading--spaced">
                                    <span class="section-number">2</span>
                                    <div>
                                        <h6 class="mb-0">{{ $t('choose_recipient') }}</h6>
                                        <small class="text-muted">{{ $t('recipient_step_help') }}</small>
                                    </div>
                                </div>
                                <div class="row g-3">
                                    <div class="col-md-4">
                                        <label class="form-label fw-semibold">{{ $t('type') }}</label>
                                        <select v-model="contract.type" class="form-select" @change="onTypeChange">
                                            <option v-for="type in availableContractTypes" :key="type.value" :value="type.value">
                                                {{ $t(type.label) }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="col-md-8">
                                        <label class="form-label fw-semibold">{{ $t('linked_entity') }} <span class="text-danger">*</span></label>
                                        <auto-complete
                                            v-if="contract.type"
                                            ref="entityAutocomplete"
                                            :key="componentKey"
                                            :entity="contract.type + 's'"
                                            :defaultValue="entityName"
                                            :dislaySuggestions="true"
                                            emitEvent="emitAutoCompleteEntity"
                                            @emitAutoCompleteEntity="emitAutoCompleteEntity"
                                            class="p-0 border-0"
                                        ></auto-complete>
                                        <button
                                            v-if="contract.type === 'client'"
                                            type="button"
                                            class="btn btn-link btn-sm px-0 mt-1 text-decoration-none related-create-link"
                                            @click="openCreateClientModal"
                                        >
                                            <AppIcon name="bi-plus-circle" class="bi bi-plus-circle me-1" />{{ $t('create_new_client') }}
                                        </button>
                                    </div>
                                </div>
                                <div v-if="selectedEntityData" class="recipient-confirmation">
                                    <span class="recipient-avatar">{{ entityInitials }}</span>
                                    <strong>{{ entityName }}</strong>
                                    <AppIcon name="bi-check-circle-fill" class="bi bi-check-circle-fill text-success ms-auto" />
                                </div>

                                <button
                                    type="button"
                                    class="optional-toggle"
                                    :aria-expanded="showOptionalDetails ? 'true' : 'false'"
                                    @click="showOptionalDetails = !showOptionalDetails"
                                >
                                    <span>
                                        <AppIcon name="bi-sliders" class="bi bi-sliders me-2" />
                                        {{ showOptionalDetails ? $t('hide_optional_details') : $t('show_optional_details') }}
                                    </span>
                                    <AppIcon :name="showOptionalDetails ? 'bi-chevron-up' : 'bi-chevron-down'" :class="['bi', showOptionalDetails ? 'bi-chevron-up' : 'bi-chevron-down']" />
                                </button>

                                <div v-show="showOptionalDetails" class="optional-details">
                                    <div class="row g-3">
                                        <div class="col-md-6">
                                            <label class="form-label small fw-semibold">{{ $t('contract_number') }}</label>
                                            <input v-model="contract.contract_number" type="text" class="form-control" :placeholder="$t('auto_generated_if_empty')">
                                        </div>
                                        <div class="col-md-6">
                                            <label class="form-label small fw-semibold">{{ $t('end_date') }}</label>
                                            <input v-model="contract.end_date" type="date" class="form-control" :min="contract.start_date">
                                        </div>
                                        <div class="col-md-4">
                                            <label class="form-label small fw-semibold">{{ $t('amount') }}</label>
                                            <input v-model="contract.amount" type="number" min="0" step="0.01" class="form-control">
                                        </div>
                                        <div class="col-md-4">
                                            <label class="form-label small fw-semibold">{{ $t('billing_frequency') }}</label>
                                            <select v-model="contract.billing_frequency" class="form-select">
                                                <option value="monthly">{{ $t('monthly') }}</option>
                                                <option value="yearly">{{ $t('yearly') }}</option>
                                                <option value="one_time">{{ $t('one_time') }}</option>
                                            </select>
                                        </div>
                                        <div class="col-md-4">
                                            <label class="form-label small fw-semibold">{{ $t('status') }}</label>
                                            <select v-model="contract.status" class="form-select">
                                                <option value="active">{{ $t('active') }}</option>
                                                <option value="pending">{{ $t('pending') }}</option>
                                                <option value="expired">{{ $t('expired') }}</option>
                                                <option value="terminated">{{ $t('terminated') }}</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </section>
                    </div>

                    <div v-if="activeTab === 'content'">
                        <ContractSectionBuilder
                            ref="contractBuilder"
                            v-model="contract.content"
                            :variables="availableVariables"
                            :variableValues="previewValues"
                            :allow-smart-variables="isTemplateMode"
                            :previewName="entityName"
                            :pagePadding.sync="pagePadding"
                            :pageMargin.sync="pageMargin"
                        />
                    </div>
                </div>

                <div class="modal-footer border-0">
                    <button v-if="activeTab === 'general' && !contract.id && !isTemplateMode" type="button" class="btn btn-light me-auto" @click="activeTab = 'template'">
                        <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-1" />{{ $t('back') }}
                    </button>
                    <button v-if="activeTab === 'content'" type="button" class="btn btn-light me-auto" @click="activeTab = 'general'">
                        <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-1" />{{ $t('back_to_details') }}
                    </button>
                    <button type="button" class="btn btn-light" data-bs-dismiss="modal">
                        {{ $t('close') }}
                    </button>
                    <button
                        v-if="activeTab === 'template'"
                        type="button"
                        class="btn btn-primary px-4"
                        :disabled="!selectedTemplateId"
                        @click="continueToRecipient"
                    >
                        {{ $t('next') }}<AppIcon name="bi-arrow-right" class="bi bi-arrow-right ms-2" />
                    </button>
                    <button
                        v-if="activeTab === 'general'"
                        type="button"
                        class="btn btn-primary px-4"
                        :disabled="!setupValid"
                        @click="switchToContentTab"
                    >
                        {{ isTemplateMode ? $t('continue_to_builder') : $t('review_contract') }}<AppIcon name="bi-arrow-right" class="bi bi-arrow-right ms-2" />
                    </button>
                    <button
                        v-if="activeTab === 'content'"
                        type="button"
                        class="btn btn-primary px-4"
                        :disabled="loading || !setupValid"
                        @click="save"
                    >
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        <AppIcon name="bi-check-circle" v-else class="bi bi-check-circle me-2" />
                        {{ contract.id ? $t('update_contract') : $t('create_contract') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
    <add-client
        v-if="createClientState"
        :state="createClientState"
        @createClient="createClient"
    />
  </div>
</template>

<script>
import helper from '../../helper'
import ContractSectionBuilder from './ContractSectionBuilder.vue'
import AutoComplete from '../autocomplete/AutoComplete'
import AddClient from '../AddClient.vue'

export default {
  name: 'AddEditContractModal',
  components: {
    ContractSectionBuilder,
    AutoComplete,
    AddClient
  },
  props: ['contractData'],
  data(){
    return {
        activeTab: 'general',
        selectedTemplateId: 'preset:blank',
        selectedTemplateApplied: false,
        templatesLoading: false,
        showOptionalDetails: false,
        pagePadding: 20,
        pageMargin: 20,
        contract: {
            id: null,
            title: '',
            contract_number: '',
            type: 'client',
            linked_id: '',
            start_date: new Date().toISOString().substr(0, 10),
            end_date: '',
            amount: 0,
            status: 'active',
            billing_frequency: 'monthly',
            auto_renew: false,
            content: ''
        },
        entityName: '',
        componentKey: 0,
        createModal: null,
        createClientState: false,
        loading: false,
        availableVariables: [
            { key: '{{business.name}}', label: 'Business Name' },
            { key: '{{business.email}}', label: 'Business Email' },
            { key: '{{business.phone}}', label: 'Business Phone' },
            { key: '{{business.address}}', label: 'Business Address' },
            { key: '{{contract.contract_number}}', label: 'Contract Number' },
            { key: '{{contract.title}}', label: 'Contract Title' },
            { key: '{{contract.start_date}}', label: 'Start Date' },
            { key: '{{contract.end_date}}', label: 'End Date' },
            { key: '{{contract.amount}}', label: 'Amount' },
            { key: '{{entity.name}}', label: 'Entity Name' },
            { key: '{{entity.email}}', label: 'Entity Email' },
            { key: '{{entity.phone}}', label: 'Entity Phone' },
            { key: '{{entity.address}}', label: 'Entity Address' },
            { key: '{{employee.hire_date}}', label: 'Employee Hire Date' },
            { key: '{{current_date}}', label: 'Current Date' },
            { key: '{{date}}', label: 'Current Date' }
        ],
        isTemplateMode: false,
        templates: [],
        selectedEntityData: null
    }
  },
  computed: {
    currentStep() {
        return { template: 1, general: 2, content: 3 }[this.activeTab] || 1;
    },
    creationSteps() {
        return [
            { key: 'template', label: this.$t('select_template') },
            { key: 'recipient', label: this.$t('choose_recipient') },
            { key: 'preview', label: this.$t('preview') }
        ];
    },
    modalSubtitle() {
        if (this.isTemplateMode) {
            return this.activeTab === 'content'
                ? this.$t('contract_content_help')
                : this.$t('contract_setup_help');
        }

        if (this.activeTab === 'template') return this.$t('choose_contract_template_help');
        if (this.activeTab === 'content') return this.$t('review_contract_help');
        return this.$t('recipient_step_help');
    },
    templateChoices() {
        const starterLayouts = [
            {
                value: 'preset:blank',
                label: this.$t('blank_contract'),
                description: this.$t('blank_layout_description'),
                icon: 'bi-file-earmark',
                preview: 'blank'
            },
            {
                value: 'preset:contract-business',
                label: this.$t('contract_business'),
                description: this.$t('contract_business_layout_description'),
                icon: 'bi-building',
                preview: 'business'
            },
            {
                value: 'preset:contract-executive',
                label: this.$t('contract_executive'),
                description: this.$t('contract_layout_description'),
                icon: 'bi-briefcase',
                preview: 'executive'
            },
            {
                value: 'preset:contract-modern',
                label: this.$t('contract_modern'),
                description: this.$t('contract_layout_description'),
                icon: 'bi-layout-text-window-reverse',
                preview: 'modern'
            }
        ];
        const savedTemplates = this.templates.map(template => ({
            value: `template:${template.id}`,
            label: template.title,
            description: this.$t('saved_template_description'),
            icon: 'bi-file-earmark-text',
            preview: 'saved'
        }));

        return [...starterLayouts, ...savedTemplates];
    },
    availableContractTypes() {
        const modules = this.$store.getters.MODULES || [];
        const moduleEnabled = itemTitle => modules.some(module => (
            module.enabled === true &&
            (module.items || []).some(item => item.title === itemTitle && item.enabled === true)
        ));

        return [
            { value: 'client', label: 'client', moduleItem: 'clients' },
            { value: 'supplier', label: 'supplier', moduleItem: 'suppliers' },
            { value: 'employee', label: 'employee', moduleItem: 'employees' }
        ].filter(type => moduleEnabled(type.moduleItem));
    },
    setupValid() {
        if (this.isTemplateMode) return Boolean(this.contract.title);
        return Boolean(
            this.contract.title &&
            this.contract.type &&
            (this.contract.id || this.contract.linked_id) &&
            this.contract.start_date &&
            this.contract.amount >= 0 &&
            this.contract.status
        );
    },
    isValid() {
        return this.setupValid;
    },
    entityInitials() {
        const name = this.entityName || ''
        return name
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map(part => part.charAt(0).toUpperCase())
            .join('') || '?';
    },
    previewValues() {
        const business = this.$store.getters.BUSINESS || {};
        const entity = this.selectedEntityData || {};
        const entityName = entity.name || (
            entity.first_name
                ? `${entity.first_name} ${entity.last_name || ''}`.trim()
                : this.entityName
        );
        const currentDate = this.formatVariableDate(new Date());

        return {
            '{{business.name}}': business.name || this.$t('business_name'),
            '{{business.email}}': business.email || this.$t('email'),
            '{{business.phone}}': business.phone || this.$t('phone'),
            '{{business.address}}': business.address || this.$t('address'),
            '{{contract.contract_number}}': this.contract.contract_number || this.$t('auto_generated'),
            '{{contract.title}}': this.contract.title || this.$t('contract'),
            '{{contract.start_date}}': this.formatVariableDate(this.contract.start_date),
            '{{contract.end_date}}': this.formatVariableDate(this.contract.end_date),
            '{{contract.amount}}': this.contract.amount || '0',
            '{{entity.name}}': entityName || this.$t('recipient_name'),
            '{{entity.email}}': entity.email || this.$t('email'),
            '{{entity.phone}}': entity.phone || this.$t('phone'),
            '{{entity.address}}': entity.address || this.$t('address'),
            '{{employee.hire_date}}': this.formatVariableDate(entity.hire_date),
            '{{current_date}}': currentDate,
            '{{date}}': currentDate
        };
    }
  },
  watch: {
    contractData: {
        handler(val) {
            if (val) {
                this.loadContract(val);
            } else {
                this.resetForm();
            }
        },
        immediate: true
    },
    availableContractTypes: {
        handler(types) {
            if (!this.contract || this.contract.id || types.some(type => type.value === this.contract.type)) return;
            this.contract.type = types.length ? types[0].value : '';
            this.onTypeChange();
        },
        immediate: true
    }
  },
  mounted(){
    // eslint-disable-next-line no-undef
    this.createModal = new bootstrap.Modal(document.getElementById('contract-create-modal'), {backdrop: 'static', keyboard: false})
    
    document.getElementById('contract-create-modal').addEventListener('hidden.bs.modal', () => { 
        this.$emit('closed');
    })
  },
  methods: {
    formatVariableDate(date) {
        return date ? helper.formatDate(date, false, 'DD/MM/YYYY') : '';
    },
    async switchToContentTab() {
        this.activeTab = 'content';
        await this.$nextTick();
        const builder = this.$refs.contractBuilder;
        if (!builder) return;

        if (this.selectedTemplateId.startsWith('preset:') && !this.selectedTemplateApplied) {
            const presetKey = this.selectedTemplateId.replace('preset:', '');
            const applied = await builder.applyPreset(presetKey);
            this.selectedTemplateApplied = applied !== false;
        }

        this.$nextTick(() => {
            const builder = this.$refs.contractBuilder;
            if (!builder || typeof builder.setViewMode !== 'function') return;
            builder.setViewMode(this.isTemplateMode ? 'visual' : 'preview');
        });
    },
    show(tab = 'general', isTemplate = false) {
        this.isTemplateMode = isTemplate;
        this.resetForm(); // Ensure clean state

        if (this.contractData) {
             this.isTemplateMode = !!this.contractData.is_template;
             this.loadContract(this.contractData);
        } else {
            this.contract.is_template = isTemplate;
        }

        if (!isTemplate && !this.contract.id) {
            this.fetchTemplates();
        }

        const canOpenContentDirectly = Boolean(this.contract.id && tab === 'content' && this.setupValid);
        if (canOpenContentDirectly) {
            this.activeTab = 'content';
        } else if (!this.contract.id && !this.isTemplateMode) {
            this.activeTab = 'template';
        } else {
            this.activeTab = 'general';
        }
        
        this.createModal.show();
        if (!this.contract.id) {
             this.componentKey += 1;
        }
    },
    hide() {
        this.createModal.hide();
    },
    resetForm() {
        this.activeTab = this.isTemplateMode ? 'general' : 'template';
        this.selectedTemplateId = 'preset:blank';
        this.selectedTemplateApplied = false;
        this.showOptionalDetails = false;
        this.pagePadding = 20;
        this.pageMargin = 20;
        this.contract = this.createDefaultContract();
        this.entityName = '';
        this.selectedEntityData = null;
        this.createClientState = false;
    },
    createDefaultContract() {
        return {
            id: null,
            title: '',
            contract_number: '',
            type: this.availableContractTypes.length ? this.availableContractTypes[0].value : '',
            linked_id: '',
            start_date: new Date().toISOString().substr(0, 10),
            end_date: '',
            amount: 0,
            status: 'active',
            billing_frequency: 'monthly',
            auto_renew: false,
            content: '',
            is_template: this.isTemplateMode
        };
    },
    loadContract(data) {
        this.contract = { ...this.createDefaultContract(), ...data };
        this.selectedTemplateApplied = true;
        this.showOptionalDetails = Boolean(this.contract.id);
        this.pagePadding = 20;
        this.pageMargin = 20;

        if (this.contract.start_date) this.contract.start_date = helper.formatDate(this.contract.start_date);
        if (this.contract.end_date) this.contract.end_date = helper.formatDate(this.contract.end_date);

        if (this.contract.content && this.contract.content.includes('contract-page-wrapper')) {
            const parser = new DOMParser();
            const document = parser.parseFromString(this.contract.content, 'text/html');
            const wrapper = document.querySelector('.contract-page-wrapper');
            if (wrapper) {
                const parsedPadding = parseInt(wrapper.style.padding, 10);
                const parsedMargin = parseInt(wrapper.style.margin, 10);
                this.pagePadding = Number.isNaN(parsedPadding) ? 20 : parsedPadding;
                this.pageMargin = Number.isNaN(parsedMargin) ? 20 : parsedMargin;
                this.contract.content = wrapper.innerHTML;
            }
        }

        if (!this.contract.linked_id) {
            this.entityName = '';
            this.selectedEntityData = null;
            return;
        }

        const includedEntity = this.contract.type === 'client'
            ? data.client
            : this.contract.type === 'supplier'
                ? data.supplier
                : data.employee;

        if (includedEntity) {
            this.entityName = includedEntity.name || '';
            this.selectedEntityData = includedEntity;
            this.componentKey += 1;
        } else {
            this.fetchEntityName();
        }
    },
    onTypeChange() {
        this.contract.linked_id = '';
        this.entityName = '';
        this.selectedEntityData = null;
        this.componentKey += 1; // Re-render autocomplete when type changes
    },
    emitAutoCompleteEntity(data) {
        if (data && data.result) {
            this.contract.linked_id = String(data.result.id);
            this.entityName = data.result.name || '';
            this.selectedEntityData = data.result;
        } else if (data && data.clear_input) {
            this.contract.linked_id = '';
            this.entityName = '';
            this.selectedEntityData = null;
        }
    },
    openCreateClientModal() {
        this.createClientState = true;
    },
    createClient(data) {
        if (data && data.client) {
            this.contract.type = 'client';
            this.contract.linked_id = String(data.client.id);
            this.entityName = data.client.name || '';
            this.selectedEntityData = data.client;
            this.componentKey += 1;
        }
        if (data && data.state !== undefined) {
            this.createClientState = data.state;
        }
    },
    async fetchEntityName() {
         let url = '';
        
        // Skip for templates or invalid linked_id
        if (this.contract.is_template || !this.contract.linked_id || this.contract.linked_id === '00000000-0000-0000-0000-000000000000') return;

        if (this.contract.type === 'client') url = `${process.env.VUE_APP_API}/clients/${this.contract.linked_id}`;
        else if (this.contract.type === 'supplier') url = `${process.env.VUE_APP_API}/suppliers/${this.contract.linked_id}`;
        else if (this.contract.type === 'employee') url = `${process.env.VUE_APP_API}/employees/${this.contract.linked_id}`;
        
        if (url) {
            try {
                const response = await helper.customGet(url);
                // API returns wrapped responses: { client: {...}, ... }, { supplier: {...}, ... }, { employee: {...} }
                const entity = response.data[this.contract.type] || response.data;
                this.entityName = entity.name || '';
                this.selectedEntityData = entity;
                this.componentKey += 1; // Force update autocomplete with new default value
            } catch (e) {
                alert(e);
            }
        }
    },
    async fetchTemplates() {
        if (this.templatesLoading) return;

        try {
            this.templatesLoading = true;
            const url = `${process.env.VUE_APP_API}/contracts?is_template=true&per_page=100`;
            const response = await helper.customGet(url);
            this.templates = response.data.data;
        } catch (error) {
            alert(error);
        } finally {
            this.templatesLoading = false;
        }
    },
    continueToRecipient() {
        if (!this.selectedTemplateId) return;
        this.activeTab = 'general';
    },
    selectTemplateChoice(value) {
        this.selectedTemplateId = value;
        this.onTemplateSelect();
    },
    onTemplateSelect() {
        this.selectedTemplateApplied = false;

        if (this.selectedTemplateId.startsWith('preset:')) {
            this.contract.title = '';
            this.contract.content = '';
            this.pagePadding = 20;
            this.pageMargin = 20;
            return;
        }

        const selectedId = this.selectedTemplateId.replace('template:', '');
        const template = this.templates.find(item => String(item.id) === selectedId);
        if (template) {
            this.contract.title = template.title; 
            this.contract.content = template.content;
            this.contract.billing_frequency = template.billing_frequency;
            this.contract.auto_renew = template.auto_renew;
            this.contract.amount = template.amount;
            
            // Helper to parse content if needed, like in watcher
            if (this.contract.content && this.contract.content.includes('contract-page-wrapper')) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(this.contract.content, 'text/html');
                const wrapper = doc.querySelector('.contract-page-wrapper');
                if (wrapper) {
                    const parsedPadding = parseInt(wrapper.style.padding, 10);
                    const parsedMargin = parseInt(wrapper.style.margin, 10);
                    this.pagePadding = Number.isNaN(parsedPadding) ? 20 : parsedPadding;
                    this.pageMargin = Number.isNaN(parsedMargin) ? 20 : parsedMargin;
                    this.contract.content = wrapper.innerHTML;
                } 
            }
            this.selectedTemplateApplied = true;
        }
    },
    async save(){
        if (!this.isValid) {
            this.activeTab = 'general';
            return;
        }

        try {
            const builder = this.$refs.contractBuilder;
            if (builder && typeof builder.flushStructuredEdits === 'function') {
                builder.flushStructuredEdits();
            }
            this.loading = true;
            
            // Wrap content
            const contentToSave = !this.contract.is_template
                ? this.resolveVariablesForSave(this.contract.content)
                : this.contract.content;
            const wrappedContent = `<div class="contract-page-wrapper" style="padding: ${this.pagePadding}px; margin: ${this.pageMargin}px;">${contentToSave}</div>`;
            const payload = { ...this.contract, content: wrappedContent };
            
            if (!payload.end_date) payload.end_date = null;
            if (!payload.linked_id) payload.linked_id = null;


            let response;
            if (this.contract.id) {
                // Use customPatch instead of customPut as customPut is not defined in helper
                response = await helper.customPatch(`${process.env.VUE_APP_API}/contracts/${this.contract.id}`, payload, {showToast: true});
            } else {
                delete payload.id;
                response = await helper.customPost(`${process.env.VUE_APP_API}/contracts`, payload, {showToast: true});
            }
            
            this.loading = false;
            this.$emit('saved', response.data);
            this.hide();
        } catch (error) {
            this.loading = false;
            alert(error);
        }
    },
    resolveVariablesForSave(content) {
        let resolvedContent = content || '';
        const entityFields = ['name', 'email', 'phone', 'address'];
        const replacements = { ...this.previewValues };

        entityFields.forEach(field => {
            const value = this.previewValues[`{{entity.${field}}}`] || '';
            ['client', 'customer', 'supplier', 'employee'].forEach(prefix => {
                replacements[`{{${prefix}.${field}}}`] = value;
            });
        });

        Object.keys(replacements).forEach(key => {
            const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const replacementValue = replacements[key] !== undefined && replacements[key] !== null
                ? this.escapeVariableValue(replacements[key])
                : '';
            resolvedContent = resolvedContent.replace(
                new RegExp(escapedKey, 'g'),
                replacementValue
            );
        });

        return resolvedContent;
    },
    escapeVariableValue(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }
  }
}
</script>

<style scoped>
.contract-dialog {
    transition: max-width 0.2s ease;
}

.contract-dialog--setup {
    width: calc(100% - 28px);
    max-width: 900px;
}

.contract-dialog--editor {
    width: 100vw;
    max-width: none;
    height: 100vh;
    margin: 0;
}

.contract-dialog .modal-content {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 36px);
    overflow: hidden;
    border: 0;
    border-radius: 16px;
    box-shadow: 0 24px 70px rgba(21, 31, 50, 0.18);
}

.contract-dialog--editor .modal-content {
    height: 100vh;
    max-height: none;
    border-radius: 0;
}

.contract-dialog .modal-header {
    flex: 0 0 auto;
    padding: 18px 24px;
}

.contract-modal-header {
    align-items: flex-start;
    background: #fff;
    border-bottom: 1px solid #edf0f4;
}

.modal-title-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    color: #004aad;
    background: #eef5ff;
    border-radius: 11px;
}

.modal-title-icon .bi {
    margin: 0 !important;
}

.modal-subtitle {
    max-width: 620px;
    color: #64748b;
    font-size: 0.84rem;
    line-height: 1.4;
}

.contract-progress {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    padding: 10px 24px;
    background: #fff;
    border-bottom: 1px solid #edf0f4;
}

.contract-progress-step {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #8a94a6;
    font-size: 0.78rem;
    font-weight: 600;
}

.contract-progress-step.active,
.contract-progress-step.complete {
    color: #004aad;
}

.contract-progress-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    color: #64748b;
    background: #eef1f5;
    border-radius: 50%;
}

.contract-progress-step.active .contract-progress-number,
.contract-progress-step.complete .contract-progress-number {
    color: #fff;
    background: #004aad;
}

.contract-progress-line {
    width: 56px;
    height: 2px;
    margin: 0 12px;
    background: #e1e6ed;
}

.contract-progress-line.complete {
    background: #004aad;
}

.optional-badge {
    display: inline-block;
    padding: 2px 7px;
    color: #64748b;
    font-size: 0.68rem;
    font-weight: 600;
    line-height: 1.4;
    vertical-align: middle;
    background: #eef1f5;
    border-radius: 999px;
}

.contract-dialog .modal-footer {
    flex: 0 0 auto;
    min-height: 68px;
    padding: 12px 24px;
    background: #fff;
    border-top: 1px solid #e7eaf0 !important;
    box-shadow: 0 -5px 18px rgba(31, 38, 55, 0.04);
}

.contract-modal-body {
    flex: 1 1 auto;
    min-height: 0;
    max-height: none;
    padding: 20px 24px;
    overflow-y: auto;
    background: #f8f9fc;
}

.setup-workspace {
    width: 100%;
    max-width: 1050px;
    margin: 0 auto;
}

.template-step-workspace {
    width: 100%;
    max-width: 1240px;
    margin: 0 auto;
}

.step-intro {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 20px;
    color: #64748b;
}

.step-intro h5 {
    color: #1e293b;
}

.step-intro-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    color: #004aad;
    font-size: 1.1rem;
    background: #eaf3ff;
    border-radius: 12px;
}

.template-loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 300px;
    color: #64748b;
}

.template-choice-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(245px, 1fr));
    gap: 18px;
}

.template-choice-card {
    position: relative;
    display: flex;
    min-height: 330px;
    padding: 16px;
    overflow: hidden;
    flex-direction: column;
    color: #334155;
    text-align: start;
    background: #fff;
    border: 2px solid transparent;
    border-radius: 14px;
    box-shadow: 0 4px 18px rgba(31, 41, 55, 0.08);
    transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.template-choice-card:hover {
    transform: translateY(-2px);
    border-color: #a9c9f3;
    box-shadow: 0 10px 28px rgba(31, 41, 55, 0.12);
}

.template-choice-card.selected {
    border-color: #004aad;
    box-shadow: 0 0 0 3px rgba(0, 74, 173, 0.1), 0 10px 28px rgba(31, 41, 55, 0.1);
}

.template-choice-preview {
    position: relative;
    display: flex;
    height: 220px;
    padding: 28px 24px;
    overflow: hidden;
    flex-direction: column;
    gap: 12px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 9px;
    box-shadow: inset 0 0 0 8px #f8fafc;
}

.template-choice-preview span {
    display: block;
    height: 6px;
    background: #d7dee8;
    border-radius: 999px;
}

.template-choice-preview .preview-header-line {
    width: 34%;
    height: 14px;
    margin-bottom: 18px;
    background: #004aad;
}

.template-choice-preview .preview-title-line {
    width: 70%;
    height: 9px;
    background: #64748b;
}

.template-choice-preview .preview-copy-line {
    width: 100%;
}

.template-choice-preview .preview-copy-line.short {
    width: 74%;
}

.template-choice-preview .preview-signature-line {
    width: 42%;
    margin-top: auto;
}

.template-choice-preview--blank span {
    opacity: 0.18;
}

.template-choice-preview--business {
    border-top: 7px solid #004aad;
}

.template-choice-preview--executive .preview-header-line {
    width: 100%;
    background: #1e293b;
}

.template-choice-preview--modern {
    box-shadow: inset 11px 0 0 #004aad, inset 0 0 0 8px #f8fafc;
}

.template-choice-preview--saved .preview-header-line {
    background: #7c3aed;
}

.template-choice-content {
    display: flex;
    padding-top: 15px;
    flex-direction: column;
    gap: 7px;
}

.template-choice-title {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #1e293b;
}

.template-choice-content small {
    color: #64748b;
    line-height: 1.45;
}

.template-choice-check {
    position: absolute;
    top: 10px;
    right: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    color: #fff;
    background: #004aad;
    border: 3px solid #fff;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0, 74, 173, 0.25);
}

.setup-card {
    padding: 22px;
    background: #fff;
    border: 1px solid #e5e8f0;
    border-radius: 14px;
    box-shadow: 0 4px 16px rgba(38, 45, 62, 0.035);
}

.template-picker {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin: -2px -2px 22px;
    padding: 14px;
    background: #f8fafc;
    border: 1px solid #e8edf3;
    border-radius: 12px;
}

.template-picker-label {
    display: flex;
    align-items: center;
    gap: 11px;
}

.template-picker-control {
    width: min(100%, 330px);
}

.setup-card-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    color: #303030;
    background: #f1f1f1;
    border-radius: 10px;
}

.section-heading {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
}

.section-heading--spaced {
    margin-top: 24px;
}

.section-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
    color: #004aad;
    font-size: 0.78rem;
    font-weight: 700;
    background: #eef5ff;
    border-radius: 8px;
}

.recipient-confirmation {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    padding: 9px 10px;
    background: #f7f9fd;
    border: 1px solid #e4e8f1;
    border-radius: 10px;
}

.recipient-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    color: #fff;
    font-size: 0.72rem;
    font-weight: 700;
    background: #303030;
    border-radius: 50%;
}

.optional-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-top: 22px;
    padding: 11px 13px;
    color: #475569;
    font-size: 0.84rem;
    font-weight: 600;
    text-align: start;
    background: transparent;
    border: 1px dashed #cbd5e1;
    border-radius: 10px;
}

.optional-toggle:hover {
    color: #004aad;
    background: #f8fafc;
    border-color: #94a3b8;
}

.optional-details {
    margin-top: 14px;
    padding: 16px;
    background: #f8fafc;
    border: 1px solid #e8edf3;
    border-radius: 10px;
}

.contract-modal-host .btn-primary {
    background-color: #004aad;
    border-color: #004aad;
}

.contract-modal-host .btn-primary:hover,
.contract-modal-host .btn-primary:focus {
    background-color: #1f1f1f;
    border-color: #1f1f1f;
}

.contract-modal-host .btn-outline-primary {
    color: #004aad;
    border-color: #004aad;
}

.contract-modal-host .btn-outline-primary:hover,
.contract-modal-host .btn-outline-primary:focus {
    color: #fff;
    background: #004aad;
    border-color: #004aad;
}

.contract-modal-host .btn-link {
    color: #334155;
}

.contract-modal-host .form-control:focus,
.contract-modal-host .form-select:focus {
    border-color: #303030;
    box-shadow: 0 0 0 0.2rem rgba(48, 48, 48, 0.16);
}

@media (max-width: 767.98px) {
    .contract-dialog--setup {
        width: calc(100% - 16px);
        margin: 8px auto;
    }

    .contract-dialog--setup .modal-content {
        max-height: calc(100vh - 16px);
        border-radius: 12px;
    }

    .contract-dialog .modal-header,
    .contract-dialog .modal-footer,
    .contract-modal-body {
        padding-right: 14px;
        padding-left: 14px;
    }

    .contract-progress {
        justify-content: flex-start;
        padding-right: 14px;
        padding-left: 14px;
        overflow-x: auto;
    }

    .contract-progress-step {
        white-space: nowrap;
    }

    .contract-progress-line {
        width: 24px;
        min-width: 24px;
        margin: 0 8px;
    }

    .setup-card {
        padding: 16px;
    }

    .template-choice-grid {
        grid-template-columns: 1fr;
    }

    .template-choice-card {
        min-height: 285px;
    }

    .template-choice-preview {
        height: 180px;
    }

    .template-picker {
        align-items: stretch;
        flex-direction: column;
        gap: 12px;
    }

    .template-picker-control {
        width: 100%;
    }

    .contract-dialog .modal-footer {
        gap: 8px;
    }

    .contract-dialog .modal-footer .btn {
        flex: 1 1 auto;
    }
}
</style>
